import { css, HandlesIcon, spaceToPx, styled } from 'canoe';
import React, { useEffect } from 'react';
import { DragPreviewImage, useDrag, useDrop } from 'react-dnd';

import { ValueCard } from '../ValueCard';
import { ValueCardProps } from '../ValueCard/ValueCard';

const DraggableCardContainer = styled.div<{
  placeholderDirection?: 'up' | 'down' | false;
  ref: any;
}>`
  ${({ theme, placeholderDirection }) => css`
    position: relative;
    padding: ${theme.spaces.xxs} 0;

    &::after,
    &::before {
      content: '';
      height: 1px;
      width: 100%;
      background: transparent;
      position: absolute;
      left: 0;
      transition: background 0.25s ease-in-out;
    }

    &::after {
      bottom: 0;
    }

    &::before {
      top: 0;
    }

    ${placeholderDirection === 'up' &&
    css`
      &::before {
        background: ${theme.colors.BrandPurple};
      }
    `}

    ${placeholderDirection === 'down' &&
    css`
      &::after {
        background: ${theme.colors.BrandPurple};
      }
    `}
  `}
`;

const StyledCard = styled(ValueCard)<{
  isDragging?: boolean;
}>`
  ${({ isDragging }) => css`
    ${isDragging &&
    css`
      opacity: 0.5;
    `}
  `}
`;

const DraggableIndicatorIcon = styled(HandlesIcon)`
  // the only way to replicate designs?
  height: 14px;
`;
const DraggableIndicator = styled.div<{
  horizontalSpace: 'xs' | 'xxs';
  ref: any;
}>`
  ${({ theme, horizontalSpace }) => css`
    width: ${horizontalSpace === 'xs' ? 16 : 16 - spaceToPx(horizontalSpace)}px;
    padding: 0 ${theme.spaces[horizontalSpace]} 0 0;
    cursor: grab;

    &:hover {
      ${DraggableIndicatorIcon} svg > * {
        fill: ${theme.colors.GlitterGrayD20};
      }
    }
  `}
`;

const getDragDirection = (dragItem: { index: number }, dropIndex: number) => {
  if (!dragItem) {
    return false;
  }

  if (dragItem.index > dropIndex) {
    return 'up';
  }
  if (dragItem.index < dropIndex) {
    return 'down';
  }

  return false;
};

interface DraggableValueCardProps extends ValueCardProps {
  index: number;
  draggableClassName?: string;
  onDrag?: (isDragging: boolean) => void;
  onDrop: (dragIndex: number, hoverIndex: number) => void;
  placeholderImagePath: string;
  draggableHandleHeight?: 3 | 4;
}

export const DraggableValueCard = React.memo(
  ({
    children,
    index,
    draggableClassName = 'draggable-handle',
    draggableHandleHeight = 3,
    onDrop,
    placeholderImagePath,
    horizontalSpace = 'xs',
    onDrag,
    ...rest
  }: DraggableValueCardProps) => {
    const [{ isDragging, dragItem }, dragRef, dragPreviewRef] = useDrag({
      type: 'ValueCard',
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        dragItem: monitor.getItem(),
      }),
    });

    const [{ isOver }, dropRef] = useDrop({
      accept: 'ValueCard',
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      canDrop: (item: { index: number }) => {
        const dragIndex = item.index;
        return dragIndex !== index;
      },
      drop: (item: { index: number }) => {
        const dragIndex = item.index;
        onDrop(dragIndex, index);
      },
    });

    useEffect(() => {
      onDrag?.(isDragging);
    }, [isDragging]);

    return (
      <DraggableCardContainer
        placeholderDirection={isOver && getDragDirection(dragItem, index)}
        ref={dropRef}
      >
        <DragPreviewImage connect={dragPreviewRef} src={placeholderImagePath} />
        <StyledCard isDragging={isDragging} horizontalSpace={horizontalSpace} {...rest}>
          <DraggableIndicator
            horizontalSpace={horizontalSpace}
            className={draggableClassName}
            ref={dragRef}
          >
            {new Array(draggableHandleHeight).fill(null).map((_, i) => (
              <DraggableIndicatorIcon key={i.toString()} color="GlitterGrayD10" />
            ))}
          </DraggableIndicator>
          {children}
        </StyledCard>
      </DraggableCardContainer>
    );
  },
);
